import { unwrap } from "@sagot/api-client";
import { WEBHOOK_DELIVERIES_LIMIT } from "@sagot/constants";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { useApi } from "../../../session/session-provider";
import { useWorkspaceId } from "../../../session/workspace-context";
import { wsKeys } from "../shared";

const EXPORT_ACTIVE: ReadonlySet<string> = new Set(["queued", "running"]);
const EXPORT_POLL_MS = 5000;

/** Read hooks for the Settings panels (mutations live next to the forms that use them). */
export function useOrganization() {
  const api = useApi();
  const wsId = useWorkspaceId();
  return useQuery({ queryKey: wsKeys.organization(wsId), queryFn: () => unwrap(api.GET("/v1/settings/organization")) });
}

export function useSecuritySettings() {
  const api = useApi();
  const wsId = useWorkspaceId();
  return useQuery({ queryKey: wsKeys.security(wsId), queryFn: () => unwrap(api.GET("/v1/settings/security")) });
}

export function usePrivacySettings() {
  const api = useApi();
  const wsId = useWorkspaceId();
  return useQuery({ queryKey: wsKeys.privacy(wsId), queryFn: () => unwrap(api.GET("/v1/settings/privacy")) });
}

export function useApiKeys() {
  const api = useApi();
  const wsId = useWorkspaceId();
  return useQuery({ queryKey: wsKeys.apiKeys(wsId), queryFn: () => unwrap(api.GET("/v1/api-keys")) });
}

export function useWebhooks() {
  const api = useApi();
  const wsId = useWorkspaceId();
  return useQuery({ queryKey: wsKeys.webhooks(wsId), queryFn: () => unwrap(api.GET("/v1/webhooks")) });
}

export function useDeliveries(webhookId: string | null) {
  const api = useApi();
  const wsId = useWorkspaceId();
  return useQuery({
    queryKey: wsKeys.deliveries(wsId, webhookId ?? ""),
    queryFn: () => unwrap(api.GET("/v1/webhooks/{webhook_id}/deliveries", { params: { path: { webhook_id: webhookId ?? "" }, query: { limit: WEBHOOK_DELIVERIES_LIMIT } } })),
    enabled: webhookId !== null,
  });
}

export function useIntegrations() {
  const api = useApi();
  const wsId = useWorkspaceId();
  return useQuery({ queryKey: wsKeys.integrations(wsId), queryFn: () => unwrap(api.GET("/v1/integrations")) });
}

export function useNotificationPreferences() {
  const api = useApi();
  const wsId = useWorkspaceId();
  return useQuery({ queryKey: wsKeys.notificationPrefs(wsId), queryFn: () => unwrap(api.GET("/v1/notification-preferences")) });
}

export function useCustomDomains() {
  const api = useApi();
  const wsId = useWorkspaceId();
  return useQuery({ queryKey: wsKeys.customDomains(wsId), queryFn: () => unwrap(api.GET("/v1/custom-domains")) });
}

export function useDataExports(enabled: boolean) {
  const api = useApi();
  const wsId = useWorkspaceId();
  return useQuery({
    queryKey: wsKeys.dataExports(wsId),
    queryFn: () => unwrap(api.GET("/v1/data-exports")),
    enabled,
    // Poll while the newest export is still being prepared.
    refetchInterval: (query) => (EXPORT_ACTIVE.has(query.state.data?.[0]?.status ?? "") ? EXPORT_POLL_MS : false),
  });
}

export function useSessions() {
  const api = useApi();
  return useQuery({ queryKey: wsKeys.sessions, queryFn: () => unwrap(api.GET("/v1/me/sessions")) });
}

/** Invalidate one cached key (after a mutation). */
export function useInvalidate() {
  const queryClient = useQueryClient();
  return useCallback((queryKey: readonly unknown[]) => queryClient.invalidateQueries({ queryKey }), [queryClient]);
}
